<script setup lang="ts">
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import MicButton from '@/components/common/inputs/MicButton.vue'
import { computed, ref } from 'vue'
import type { CreateCallDto } from '@/api-swagger'
import { formatDateForOdoo } from '@/utils/dateUtils'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import { useCallStore } from '@/stores/useCallStore'
import { useUserStore } from '@/stores/useUserStore'
import { PreferenceType, useUserPreferenceStore } from '@/stores/useUserPreferenceStore'

const $toast = useToast()

const callStore = useCallStore()
const { loading, loadingCreate } = storeToRefs(callStore)

const userStore = useUserStore()
const { userConnected } = storeToRefs(userStore)

const call = ref<CreateCallDto>()

interface Props {
  partnerId: number
}
const props = defineProps<Props>()

call.value = {
  callDate: formatDateForOdoo(new Date()),
  userId: userConnected.value?.id || 0,
  partnerId: props.partnerId,
  comment: '',
  doNotDisturb: false,
}

const userPreferenceStore = useUserPreferenceStore()
const prospectionButtons = computed(() => {
  const pref = userPreferenceStore.preferences.find(p => p.code === PreferenceType.PROSPECTION_BUTTONS)
  return pref?.data?.buttons ?? []
})

const appendText = (text: string) => {
  if (!call.value) return
  call.value.comment = call.value.comment ? call.value.comment + ' ' + text : text
}

const saveCall = async () => {
  loading.value = true
  try {
    if (userConnected.value && call.value) {
      call.value.userId = userConnected.value.id
      call.value.callDate = formatDateForOdoo(new Date())
    }

    if (call.value) {
      await callStore.createCall(call.value)
      $toast.success('Les modifications ont été enregistrées avec succès.')
      call.value.comment = ''
      callStore.getCallsByPartnerId(props.partnerId)
    }
  } catch (error) {
    console.error(error)
    $toast.error('Une erreur est survenue lors de la sauvegarde.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex w-full justify-center">
    <div class="self-center w-1/2">
      <div class="flex items-center gap-1 mb-2">
        <div class="font-bold">Note de l'appel</div>
        <MicButton :disabled="loading" @result="val => call && (call.comment = call.comment ? call.comment + ' ' + val : val)" />
      </div>
      <textarea
        v-if="call"
        id="call-note"
        class="textarea textarea-bordered w-full h-32 p-2 text-sm mb-2"
        v-model="call.comment"
        placeholder="Écrivez les notes de l'appel ici..."
      ></textarea>
      <div class="flex items-center justify-end gap-2">
        <button
          v-for="(btn, index) in prospectionButtons"
          :key="index"
          type="button"
          class="btn btn-sm"
          :style="{ backgroundColor: btn.color, borderColor: btn.color, color: '#fff' }"
          @click="appendText(btn.text)"
        >
          {{ btn.label }}
        </button>
        <SaveButton class="mt-0" :loading="loadingCreate" @save="saveCall" />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
